<template>
  <div
    class="upload-section pt-4 px-2 pb-2 mt-4"
    :class="{ enter: dragin }"
    @dragover.prevent="dragEnter"
    @dragleave.prevent="dragLeave"
    @drop.prevent="dropFile"
  >
    <div class="text-16 text-bold mb-3">書類アップロード</div>

    <!-- ファイル選択ボタン -->
    <div class="sub-heading flex align-end gap-8 mb-2">
      <el-button
        type="secondary"
        size="medium"
        class="choose_file"
        @click="loadFile"
      >
        Choose Local File
        <input
          ref="fileInput"
          class="file_input"
          type="file"
          multiple
          accept=".pdf"
          @change="setFile"
        >
      </el-button>
      <span class="ml-2 text-12 text-gray-600">
        ※本画面からはPDFファイルのみアップロード可能です
      </span>
    </div>

    <!-- ドラッグ＆ドロップエリア -->
    <div class="drag_and_drop_area p-6">
      <img src="@/assets/images/dropfile.svg" alt="">
      Drop files here to upload.
    </div>

    <!-- アップロード待ちファイルリスト -->
    <div v-if="uploadedDocuments.length > 0" class="uploaded-list p-2 mt-2">
      <p class="text-12 text-gray-600 mb-1">ファイル区分を選択して登録してください</p>
      <div
        v-for="(doc, index) in uploadedDocuments"
        :key="doc._uid"
        class="uploaded-item flex align-center"
      >
        <span class="text-12 mr-2">{{ doc.documentName }}</span>
        <el-select
          :value="doc.documentCategory"
          placeholder="未選択"
          size="mini"
          class="category-select mr-1"
          @change="(val) => handleCategoryChange(index, val)"
        >
          <el-option
            v-for="opt in documentCategoryOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
        <el-button
          size="mini"
          type="text"
          icon="el-icon-close"
          @click="removeFile(index)"
        />
      </div>
    </div>

    <!-- エラーメッセージ -->
    <div v-if="uploadErrorMessages" class="mt-2">
      <el-alert title="Error" type="error">
        <ul>
          <li v-for="(item, index) in uploadErrorMessages" :key="index">
            {{ item.message }}
          </li>
        </ul>
      </el-alert>
    </div>

    <!-- 登録ボタン -->
    <div class="flex mt-2">
      <el-button
        type="primary"
        size="medium"
        :disabled="!fileActionOccurred || hasUncategorizedFiles"
        :loading="isUploading"
        @click="register"
      >
        登録
      </el-button>
    </div>
  </div>
</template>

<script>
import {
  DOCUMENT_EDIT_FLG,
  PROCESS_PROGRESS_STATUS,
  PROCESS_OPERATION,
  RESPONSIBLE_FLG,
} from 'lib-tw-common';
import { $api } from '@/store/ApiClient';

export default {
  name: 'AiOcrFileUpload',
  inject: ['s'],
  data() {
    return {
      dragin: false,
      uploadIdCounter: 0,
      uploadedDocuments: [],
      existingDocuments: [],
      uploadErrorMessages: null,
      initialUpdateDate: null,
      fileActionOccurred: false,
      isUploading: false,
      documentCategoryOptions: [
        { label: 'INV', value: 'INV' },
        { label: 'P/O', value: 'P/O' },
      ],
    };
  },
  computed: {
    hasUncategorizedFiles() {
      return this.uploadedDocuments.some((doc) => !doc.documentCategory);
    },
  },
  created() {
    this.fetchUploadedFiles();
  },
  methods: {
    fetchUploadedFiles() {
      const params = {
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode:
            'get_/v1/process-documents/{entityId}/{processId}/{processSeq}',
          path: {
            entityId: this.s.entityId,
            processId: this.s.processId,
            processSeq: this.s.processSeq,
          },
        },
      };
      return $api
        .request(params)
        .then((response) => {
          if (response?.meta) {
            this.initialUpdateDate = response.meta.updateDate;
          }
          this.existingDocuments = Array.isArray(response?.documents)
            ? response.documents
            : [];
        })
        .catch((err) => {
          this.$store.dispatch('SHOW_ALERT', err.message);
        });
    },
    dragEnter() {
      this.dragin = true;
    },
    dragLeave() {
      this.dragin = false;
    },
    dropFile(e) {
      this.dragin = false;
      this.setFile(e);
    },
    loadFile() {
      this.$refs.fileInput.click();
    },
    setFile(e) {
      const files = e.target?.files?.length
        ? e.target.files
        : e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      if (files.length + this.uploadedDocuments.length > 10) {
        this.$store.dispatch(
          'SHOW_ALERT',
          'アップロードできるファイルは最大10件です。',
        );
        return;
      }
      for (const file of files) {
        if (file.name.length > 100) {
          this.$store.dispatch(
            'SHOW_ALERT',
            'ファイル名が長すぎます（100文字以内）。',
          );
          return;
        }
        if (this.s.getFileExtension(file.name) !== 'pdf') {
          this.$store.dispatch(
            'SHOW_ALERT',
            'PDFファイルのみアップロード可能です。',
          );
          return;
        }
      }

      if (typeof FileReader === 'function') {
        for (const file of files) {
          const reader = new FileReader();
          reader.onload = (event) => {
            this.uploadIdCounter += 1;
            this.uploadedDocuments.push({
              _uid: this.uploadIdCounter,
              documentSize: file.size,
              documentName: file.name,
              documentValue: event.target.result,
              documentEditFlg: DOCUMENT_EDIT_FLG.REGIST,
              documentCategory: null,
              documentCommentText: '',
              documentLinkInv: 0,
              documentLinkPl: 0,
              documentLinkSi: 0,
              documentLinkAn: 0,
              documentLinkBl: 0,
              documentLinkCo: 0,
              documentLinkOthers1: 0,
              documentLinkOthers2: 0,
              documentLinkOthers3: 0,
              documentLinkOthers4: 0,
              documentLinkOthers5: 0,
              documentLinkLabelOthers1: '',
              documentLinkLabelOthers2: '',
              documentLinkLabelOthers3: '',
              documentLinkLabelOthers4: '',
              documentLinkLabelOthers5: '',
              documentLinkContract: 0,
              documentLinkDn: 0,
              documentLinkPo: 0,
            });
          };
          reader.readAsDataURL(file);
        }
        this.fileActionOccurred = true;
        if (e.target) e.target.value = '';
      } else {
        alert('Sorry, FileReader API not supported');
      }
    },
    handleCategoryChange(index, category) {
      const doc = this.uploadedDocuments[index];
      this.$set(doc, 'documentCategory', category);
      this.$set(doc, 'documentLinkInv', category === 'INV' ? 1 : 0);
      this.$set(doc, 'documentLinkPo', category === 'P/O' ? 1 : 0);
    },
    removeFile(index) {
      this.uploadedDocuments.splice(index, 1);
      if (this.uploadedDocuments.length === 0) {
        this.fileActionOccurred = false;
      }
    },
    /**
     * POST用のdocuments配列を生成
     * 既存ドキュメント + アップロード予定ドキュメントを結合
     * （TwEditAttachedFilesSection.createPostDocuments と同じフォーマット）
     */
    createPostDocuments() {
      const convertBooleanToNumber = (value) => (value ? 1 : 0);

      const existingDocumentsRequestBody = this.existingDocuments.map(
        (document) => ({
          documentEditFlg: document.documentEditFlg,
          documentManageId: document.documentManageId,
          documentTrx: document.documentTrx,
          documentName: document.documentName,
          documentValue: document.documentValue,
          documentPath: document.documentPath,
          documentCommentText: document.documentCommentText,
          documentLinkInv: convertBooleanToNumber(document.documentLinkInv),
          documentLinkPl: convertBooleanToNumber(document.documentLinkPl),
          documentLinkSi: convertBooleanToNumber(document.documentLinkSi),
          documentLinkAn: convertBooleanToNumber(document.documentLinkAn),
          documentLinkBl: convertBooleanToNumber(document.documentLinkBl),
          documentLinkCo: convertBooleanToNumber(document.documentLinkCo),
          documentLinkOthers1: convertBooleanToNumber(
            document.documentLinkOthers1,
          ),
          documentLinkOthers2: convertBooleanToNumber(
            document.documentLinkOthers2,
          ),
          documentLinkOthers3: convertBooleanToNumber(
            document.documentLinkOthers3,
          ),
          documentLinkOthers4: convertBooleanToNumber(
            document.documentLinkOthers4,
          ),
          documentLinkOthers5: convertBooleanToNumber(
            document.documentLinkOthers5,
          ),
          documentLinkLabelOthers1: document.documentLinkLabelOthers1,
          documentLinkLabelOthers2: document.documentLinkLabelOthers2,
          documentLinkLabelOthers3: document.documentLinkLabelOthers3,
          documentLinkLabelOthers4: document.documentLinkLabelOthers4,
          documentLinkLabelOthers5: document.documentLinkLabelOthers5,
          documentLinkContract: convertBooleanToNumber(
            document.documentLinkContract,
          ),
          documentLinkDn: convertBooleanToNumber(document.documentLinkDn),
          documentLinkPo: convertBooleanToNumber(document.documentLinkPo),
        }),
      );

      const uploadedDocumentsRequestBody = this.uploadedDocuments.map(
        (document) => ({
          documentEditFlg: document.documentEditFlg,
          documentName: document.documentName,
          documentValue: document.documentValue,
          documentPath: document.documentPath,
          documentCommentText: document.documentCommentText,
          documentLinkInv: convertBooleanToNumber(document.documentLinkInv),
          documentLinkPl: convertBooleanToNumber(document.documentLinkPl),
          documentLinkSi: convertBooleanToNumber(document.documentLinkSi),
          documentLinkAn: convertBooleanToNumber(document.documentLinkAn),
          documentLinkBl: convertBooleanToNumber(document.documentLinkBl),
          documentLinkCo: convertBooleanToNumber(document.documentLinkCo),
          documentLinkOthers1: convertBooleanToNumber(
            document.documentLinkOthers1,
          ),
          documentLinkOthers2: convertBooleanToNumber(
            document.documentLinkOthers2,
          ),
          documentLinkOthers3: convertBooleanToNumber(
            document.documentLinkOthers3,
          ),
          documentLinkOthers4: convertBooleanToNumber(
            document.documentLinkOthers4,
          ),
          documentLinkOthers5: convertBooleanToNumber(
            document.documentLinkOthers5,
          ),
          documentLinkLabelOthers1: document.documentLinkLabelOthers1,
          documentLinkLabelOthers2: document.documentLinkLabelOthers2,
          documentLinkLabelOthers3: document.documentLinkLabelOthers3,
          documentLinkLabelOthers4: document.documentLinkLabelOthers4,
          documentLinkLabelOthers5: document.documentLinkLabelOthers5,
          documentLinkContract: convertBooleanToNumber(
            document.documentLinkContract,
          ),
          documentLinkDn: convertBooleanToNumber(document.documentLinkDn),
          documentLinkPo: convertBooleanToNumber(document.documentLinkPo),
        }),
      );

      return [
        ...existingDocumentsRequestBody,
        ...uploadedDocumentsRequestBody,
      ];
    },

    async register() {
      this.isUploading = true;
      this.uploadErrorMessages = null;

      // initialUpdateDate / existingDocuments が未取得の場合は取得を待つ
      // （created() での fetch が完了していないケース、
      //   またはレスポンスに meta.updateDate がなかったケースのフォールバック）
      if (!this.initialUpdateDate) {
        await this.fetchUploadedFiles();
      }

      // 工程未着手の場合は親の save フローに委譲
      // （TwEditAttachedFilesSection と同じ分岐）
      const status = this.s?.tradeManagement?.processProgressStatus;
      if (status === PROCESS_PROGRESS_STATUS.NOT_STARTED) {
        const action =
          this.s.tradeManagement.responsibleFlg === RESPONSIBLE_FLG.FROM
            ? PROCESS_OPERATION.F_SAVING
            : PROCESS_OPERATION.T_SAVING;
        this.$emit('save', action, this.createPostDocuments());
        this.uploadedDocuments = [];
        this.fileActionOccurred = false;
        this.$emit('registered');
        this.isUploading = false;
        return;
      }

      // 工程開始済の場合は process-documents に直接 POST
      const params = {
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode:
            'post_/v1/process-documents/{entityId}/{processId}/{processSeq}',
          path: {
            entityId: this.s.entityId,
            processId: this.s.processId,
            processSeq: this.s.processSeq,
          },
        },
        data: {
          meta: { updateDate: this.initialUpdateDate },
          documents: this.createPostDocuments(),
        },
      };

      this.$store.commit('START_PROCESS');
      $api
        .request(params)
        .then((response) => {
          if (response) {
            this.$store.commit('END_PROCESS');
            this.$store.dispatch('SHOW_COMPLETED');
            this.uploadedDocuments = [];
            this.fileActionOccurred = false;
            this.$emit('registered');
            this.fetchUploadedFiles();
          }
        })
        .catch((err) => {
          this.$store.commit('END_PROCESS');
          if (Array.isArray(err.response?.errors)) {
            this.uploadErrorMessages = err.response.errors;
          } else {
            this.$store.dispatch('SHOW_ALERT', err.message);
          }
        })
        .finally(() => {
          this.isUploading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-section {
  &.enter {
    outline: 2px dashed #409eff;
    outline-offset: -4px;
    background-color: #ecf5ff;
  }
}

.file_input {
  display: none;
}

.drag_and_drop_area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #c0ccda;
  border-radius: 6px;
  text-align: center;
  color: #909399;
  font-size: 13px;
  min-height: 80px;

  img {
    display: block;
    margin: 0 auto 8px;
    opacity: 0.5;
  }
}

.uploaded-list {
  background-color: #f5f7fa;
  border-radius: 4px;
}

.uploaded-item {
  padding: 2px 0;
}

.category-select {
  width: 90px;
}
</style>
